// Shared predicate-head candidate policy — the emit pipeline (P4, PH-D4).
// Consumes the engine adapter's discovered descriptors (PH-D3: the SAME
// `ValueSlotEngineAdapter`, in its no-LHS mode) and owns ambiguity, qualifier
// policy, tiers, keywords, and the no-relation-kinds invariant. Engine
// resolvers call this from their 'predicate-head' branch (P5, P6a–d).
//
// PH-D4 tiers:
//   T1 scoped columns (boolean-ish first via orderHint 0)
//   T2 qualifiers
//   T3 EXISTS / NOT EXISTS / NOT / (SELECT …) / TRUE / FALSE, and
//      boolean-returning routines
//   T4 every other routine ('_'-prefixed internals demote one further)
// Dead returns are DROPPED — the single F8 carve-out, shared from
// valueSlot/deadReturns.ts.

import type { AliasBinding, CompletionCandidate } from '@/core/engines';
import { PIVOT_SYNTHETIC_ALIAS } from '../resolver/aliasScope';
import { isDeadRoutineReturn } from '../valueSlot/deadReturns';
import {
  EXTRA_SOURCE_MIN_TIER,
  QUALIFIER_TIER,
  routineCandidateTier,
} from '../valueSlot/tiers';
import type {
  ValueSlotColumnDescriptor,
  ValueSlotEngineAdapter,
} from '../valueSlot/types';
import { emitPredicateHeadKeywords } from './keywords';
import type { PredicateHeadScope } from './types';

/**
 * Relation-class kinds that must NEVER appear at a predicate head. Unlike the
 * value slot this is not merely a ranking preference — `WHERE <table>` is
 * illegal SQL on every supported engine. The one legal unaliased-qualifier
 * case is served by kind 'alias' items below, never by kind 'table'.
 */
const FORBIDDEN_KINDS: ReadonlySet<string> = new Set([
  'table', 'view', 'schema', 'database', 'cte',
]);

/** All scoped columns sit at T1 (PH-D4) — a bare head has no LHS to coerce
 *  against, so there is no bucket signal to demote with. */
const COLUMN_TIER = 1;

/** Boolean-ish columns rank first WITHIN T1: at a predicate head a boolean
 *  column is a complete predicate by itself. */
const BOOLEANISH_ORDER_HINT = 0;

/** C1 SF-2 (shared with the value slot): pseudo-table and other legacy
 *  producers may lack `aliasSql` — fall back to the parsed alias. */
function aliasSpelling(binding: AliasBinding): string {
  return binding.aliasSql ?? binding.alias;
}

export async function emitPredicateHeadCandidates(
  out: CompletionCandidate[],
  _scope: PredicateHeadScope,
  adapter: ValueSlotEngineAdapter,
): Promise<void> {
  const columns = await adapter.collectScopedColumns();

  // Ambiguity (mirrors the value slot's D9): group by folded bare label; a
  // unique name inserts bare, a duplicated name emits one qualified candidate
  // per owner with the bare form suppressed. `WHERE customer_id` across two
  // joined bindings is as ambiguous at a head as it is at a value.
  const byName = new Map<string, ValueSlotColumnDescriptor[]>();
  for (const d of columns) {
    const key = d.label.toLowerCase();
    const group = byName.get(key);
    if (group) group.push(d);
    else byName.set(key, [d]);
  }
  for (const group of byName.values()) {
    const ambiguous = group.length > 1;
    for (const d of group) {
      out.push({
        label: ambiguous ? d.insertQualified : d.label,
        insertText: ambiguous ? d.insertQualified : d.insertBare,
        isSnippet: false,
        kind: 'column',
        tier: COLUMN_TIER,
        detail: `${d.coarseType} column`,
        // Bounded-member cap exemption (same T13a rationale as the value
        // slot): scoped columns are bounded by the STATEMENT's alias
        // bindings, never the schema, so the ranker's untyped cap must not
        // drop them behind an unbounded snapshot-routine flood.
        capExempt: true,
        ...(d.booleanish ? { orderHint: BOOLEANISH_ORDER_HINT } : {}),
      });
    }
  }

  // Qualifiers — one per distinct owner binding, kind 'alias' (a non-relation
  // kind, so the FORBIDDEN_KINDS negatives stay unconditional). The verbatim
  // alias spelling is both label and insertText; an ALIASED relation's table
  // name never appears, while an UNALIASED binding's name reaches the list
  // here as its own qualifier.
  const seenOwners = new Set<AliasBinding>();
  const seenSpellings = new Set<string>();
  const pushQualifier = (b: AliasBinding): void => {
    // An unaliased pivoted inline view gets a SYNTHETIC alias with no source
    // spelling (aliasScope: "never emitted verbatim"). It is internal — the
    // user cannot type `__pivot` — so it is not a qualifier. The clause path
    // filtered it explicitly (oracle's `emitAliasNames`); the flip's
    // owner-derived loop did not, leaking it into the head list. Synthesis is
    // engine-agnostic (T-SQL `) AS pvt` and Oracle `) p` share aliasScope), so
    // the guard lives here rather than per-adapter. Its COLUMNS still reach
    // the list above — only the untypable qualifier is suppressed.
    if (b.alias === PIVOT_SYNTHETIC_ALIAS) return;
    const spelling = aliasSpelling(b);
    seenSpellings.add(spelling);
    out.push({
      label: spelling,
      insertText: spelling,
      isSnippet: false,
      kind: 'alias',
      tier: QUALIFIER_TIER,
      detail: b.kind === 'table' || b.kind === 'view'
        ? `${b.relation} qualifier`
        : 'qualifier',
    });
  };
  for (const d of columns) {
    if (seenOwners.has(d.owner)) continue;
    seenOwners.add(d.owner);
    pushQualifier(d.owner);
  }
  // C2 head-parity — a binding whose columns could NOT be resolved (relation
  // outside the snapshot/synonyms, failed subquery inference) still names a
  // legal, user-typable qualifier: `WHERE g.col` is valid wherever the live
  // schema outruns the snapshot, and oracle's clause path offered every
  // alias at a bare `ON` via `emitAliasNames` regardless of column
  // resolution — the flip must not lose those. Adapters opt in via
  // `qualifierBindings` (which excludes internal untypable aliases); the
  // owner-derived qualifiers above keep their exact order and shape, this
  // only APPENDS the missing ones (deduped by binding identity, then by
  // spelling, mirroring emitAliasNames' by-name dedup).
  if (adapter.qualifierBindings) {
    for (const b of adapter.qualifierBindings()) {
      if (seenOwners.has(b) || seenSpellings.has(aliasSpelling(b))) continue;
      pushQualifier(b);
    }
  }

  // Routines — dead returns dropped, the rest tiered against an effective
  // BOOLEAN "LHS". Reusing `routineCandidateTier` with lhsType 'boolean' and
  // lhsResolved true yields exactly PH-D4's table: a boolean return buckets
  // 'compatible' → T3, and every other class buckets neutral/lossy → T4
  // (never T5, because the boolean row of the compat matrix has no
  // 'incompatible' cell). `_`-prefixed internals demote one tier for free.
  for (const r of adapter.collectScalarRoutines()) {
    if (isDeadRoutineReturn(r.returnsRaw)) continue;
    // PH-D4 (P1-sol F6): `returnsBooleanish` overrides the coarse class,
    // which cannot see mssql `bit` or mysql `tinyint(1)`.
    const effective = r.returnsBooleanish ? 'boolean' : r.returnsCoarse;
    out.push({
      label: r.label,
      insertText: r.insertText,
      isSnippet: r.insertText.includes('$'),
      kind: 'routine',
      tier: routineCandidateTier(adapter.engine, 'boolean', true, {
        label: r.label,
        returnsCoarse: effective,
      }),
      detail: `returns ${r.returnsRaw ?? r.returnsCoarse}`,
      triggerSignatureHelp: true,
    });
  }

  emitPredicateHeadKeywords(out, adapter.engine);

  // Engine-owned extra scalar sources (oracle sequences/packages, mssql and
  // mysql variables …), tier-clamped to ≥3 and passed through the
  // forbidden-kind filter — legality is policy-owned, not engine-owned.
  if (adapter.extraScalarSources) {
    for (const c of adapter.extraScalarSources()) {
      if (FORBIDDEN_KINDS.has(c.kind)) continue;
      out.push(c.tier < EXTRA_SOURCE_MIN_TIER ? { ...c, tier: EXTRA_SOURCE_MIN_TIER } : c);
    }
  }
}
